import React, { useEffect, useState, useMemo } from "react";
import Loader from "../../../../../components/Loader";
import { useTranslation } from "react-i18next";

export default function TypeLocalitySurvey({ data, people, setDataGraph, setFormattedHeader, filteredPosts, user }) {
    const [localitySurvey, setLocalitySurvey] = useState();
    const { t, i18n } = useTranslation();

    // 1. RECUPERATION DU FILTRE NOMINATIF
    const normalizedFilteredPosts = useMemo(() => {
        if (Array.isArray(filteredPosts)) {
            const personNames = filteredPosts
                .map(entry => entry.personName?.trim().toLowerCase())
                .filter(Boolean);
            return { personNames };
        }
        return { personNames: [] };
    }, [filteredPosts]);

    useEffect(() => {
        if (data && data.length > 0 && people) {
            handleGraphLocalitySurvey(data);
        }
    }, [data, user, filteredPosts, people]);

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    // ---- UTILITAIRES DE RECHERCHE ET DE VALIDATION ----
    const checkNameMatch = (itemName, person) => {
        if (!itemName) return false;
        const p1 = `${person.firstName || ''} ${person.lastName || ''}`.trim().toLowerCase();
        const p2 = `${person.lastName || ''} ${person.firstName || ''}`.trim().toLowerCase();
        const itemLower = itemName.toLowerCase();

        return itemLower === p1 ||
            itemLower === p2 ||
            itemLower.includes(p1) ||
            itemLower.includes(p2) ||
            p1.includes(itemLower) ||
            p2.includes(itemLower);
    };

    const getPeopleByNumberMap = (peopleFlat) => {
        const map = new Map();
        if(!peopleFlat) return map;
        peopleFlat.forEach(p => {
            [p.phone?.work, p.phone?.mobile].filter(Boolean).forEach(num => {
                if (!map.has(num)) map.set(num, []);
                map.get(num).push(p);
            });
        });
        return map;
    };

    const handleGraphLocalitySurvey = (data) => {
        const canUseCallerId = user && user.holdingId === 33;
        const personNames = normalizedFilteredPosts.personNames;
        
        const peopleFlat = people ? people.flat() : [];
        const peopleByNumber = getPeopleByNumberMap(peopleFlat);
        const knownNumbers = new Set(peopleByNumber.keys());

        let localities = {};
        
        data.forEach(item => {
            // =========================================================
            // BOUCLIER DE SÉCURITÉ : UNIQUEMENT POUR HOLDING 33
            // =========================================================
            if (canUseCallerId) {
                const isOutgoing = item.direction === "ORIGINATING";
                const isIncoming = item.direction === "TERMINATING";
                let isCallValidForUser = false;

                if (isOutgoing) {
                    const workOrMobile = item.callingnumber;
                    if (knownNumbers.has(workOrMobile)) {
                        const itemName = (item.callinglineid || "").trim();
                        const persons = peopleByNumber.get(workOrMobile) || [];

                        let matchedName = "";
                        if (itemName) {
                            const bestMatch = persons.find(p => checkNameMatch(itemName, p));
                            matchedName = bestMatch ? `${bestMatch.firstName} ${bestMatch.lastName}` : itemName;
                        } else {
                            matchedName = persons.length > 0 ? `${persons[0].firstName} ${persons[0].lastName}` : "";
                        }

                        if (personNames && personNames.length > 0) {
                            const nameToVerify = itemName ? itemName.toLowerCase() : matchedName.toLowerCase();
                            isCallValidForUser = personNames.some(filterName =>
                                nameToVerify.includes(filterName) || filterName.includes(nameToVerify)
                            );
                        } else {
                            isCallValidForUser = true; 
                        }
                    }
                } else if (isIncoming) {
                    const calledNum = item.callednumber;
                    if (knownNumbers.has(calledNum)) {
                        const itemName = (item.calledlineid || "").trim();
                        const persons = peopleByNumber.get(calledNum) || [];

                        let matchedName = "";
                        if (itemName) {
                            const bestMatch = persons.find(p => checkNameMatch(itemName, p));
                            matchedName = bestMatch ? `${bestMatch.firstName} ${bestMatch.lastName}` : itemName;
                        } else {
                            matchedName = persons.length > 0 ? `${persons[0].firstName} ${persons[0].lastName}` : "";
                        }

                        if (personNames && personNames.length > 0) {
                            const nameToVerify = itemName ? itemName.toLowerCase() : matchedName.toLowerCase();
                            isCallValidForUser = personNames.some(filterName =>
                                nameToVerify.includes(filterName) || filterName.includes(nameToVerify)
                            );
                        } else {
                            isCallValidForUser = true;
                        }
                    }
                }

                // Si le client 33 a un filtre et que l'appel ne correspond pas, on skip
                if (!isCallValidForUser && personNames.length > 0) return;
            }
            // =========================================================
            // FIN DU BOUCLIER HOLDING 33
            // =========================================================

            const location = item.location || 'Unknown';
            if (!localities[location]) {
                localities[location] = {
                    Outgoing: {
                        total: 0,
                        durationTotal: 0,
                        averageduration: 0,
                        response: 0,
                        averageresponse: 0,
                        noresponse: 0
                    },
                    Incoming: {
                        total: 0,
                        durationTotal: 0,
                        averageduration: 0,
                        response: 0,
                        averageresponse: 0,
                        noresponse: 0
                    }
                };
            }
            if (item.direction === "TERMINATING") {
                localities[location].Incoming.total += 1;
                localities[location].Incoming.durationTotal += Math.floor(parseInt(item.duration) || 0);
                if (item.answerindicator === "Yes") {
                    localities[location].Incoming.response += 1;
                    localities[location].Incoming.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                } else if (item.answerindicator === "No") {
                    localities[location].Incoming.noresponse += 1;
                }
            } else if (item.direction === "ORIGINATING") {
                localities[location].Outgoing.total += 1;
                localities[location].Outgoing.durationTotal += Math.floor(parseInt(item.duration) || 0);
                if (item.answerindicator === "Yes") {
                    localities[location].Outgoing.response += 1;
                    localities[location].Outgoing.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                } else if (item.answerindicator === "No") {
                    localities[location].Outgoing.noresponse += 1;
                }
            }
        });

        for (let location in localities) {
            let Outgoing = localities[location].Outgoing;
            let Incoming = localities[location].Incoming;

            Outgoing.averageduration = Outgoing.total > 0 ? Outgoing.durationTotal / Outgoing.total : 0;
            Outgoing.averageresponse = Outgoing.response > 0 ? Outgoing.averageresponse / Outgoing.response : 0;

            Incoming.averageduration = Incoming.total > 0 ? Incoming.durationTotal / Incoming.total : 0;
            Incoming.averageresponse = Incoming.response > 0 ? Incoming.averageresponse / Incoming.response : 0;

            Outgoing.durationTotal = formatDuration(Outgoing.durationTotal);
            Outgoing.averageduration = formatDuration(Outgoing.averageduration);
            Outgoing.averageresponse = formatDuration(Outgoing.averageresponse);

            Incoming.durationTotal = formatDuration(Incoming.durationTotal);
            Incoming.averageduration = formatDuration(Incoming.averageduration);
            Incoming.averageresponse = formatDuration(Incoming.averageresponse);
        }
        setLocalitySurvey(localities);
        const formattedData = formatForExcel(localities);
        setDataGraph(formattedData);
    }

    const formatForExcel = (data) => {
        // En-têtes des colonnes
        const headers = ["", "Sortants", "Entrants"];

        const keysMapping = [
            { key: "response", label: "Nombre d'appels répondu" },
            { key: "averageresponse", label: "Durée moyenne pour répondre" },
            { key: "noresponse", label: "Nombre d'appels non répondu" },
            { key: "durationTotal", label: "Durée totale d'appels" },
            { key: "averageduration", label: "Durée moyenne d'appels" },
        ];

        // Format des données pour Excel : première ligne = headers
        const excelData = [headers];
    
        // Parcourir chaque zone (Courbevoie, Lyon, etc.)
        Object.keys(data).forEach(zone => {
            // Ajouter une ligne pour la zone
            excelData.push([`Zone: ${zone}`, "", ""]);

            // Parcourir chaque clé et créer les lignes associées
            keysMapping.forEach(({ key, label }) => {
                const row = [
                    label,  // Utiliser la traduction pour le label
                    data[zone].Outgoing[key] !== undefined ? data[zone].Outgoing[key] : null,  // Valeur pour Sortants
                    data[zone].Incoming[key] !== undefined ? data[zone].Incoming[key] : null   // Valeur pour Entrants
                ];
                excelData.push(row);  // Ajouter la ligne à excelData
            });
        });

        setFormattedHeader(true);

        return excelData;
    };

    if (localitySurvey && Object.keys(localitySurvey).length === 0) {
        return (
            <div>{t("Aucune donnée disponible")}</div>
        );
    }

    return (
        <div className="localitySurvey">
            {localitySurvey ? (
                <div>
                    <table className="localitySurvey-sortants">
                        <thead>
                            <tr className="sortant"><th colSpan={6}>{t("Sortants")}</th></tr>
                            <tr className="sortant-head">
                                <th title={t("Zone")}>{t("Zone")}</th>
                                <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(localitySurvey).map((location, index) => (
                                <tr className="sortant-body" key={index}>
                                    <td>{location}</td>
                                    <td>{localitySurvey[location].Outgoing.response}</td>
                                    <td>{localitySurvey[location].Outgoing.averageresponse}</td>
                                    <td>{localitySurvey[location].Outgoing.noresponse}</td>
                                    <td>{localitySurvey[location].Outgoing.durationTotal}</td>
                                    <td>{localitySurvey[location].Outgoing.averageduration}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <table className="localitySurvey-entrants">
                        <thead>
                            <tr className="entrant"><th colSpan={5}>{t("Entrants")}</th></tr>
                            <tr>
                                <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(localitySurvey).map((location, index) => (
                                <tr key={index}>
                                    <td>{localitySurvey[location].Incoming.response}</td>                            
                                    <td>{localitySurvey[location].Incoming.averageresponse}</td>
                                    <td>{localitySurvey[location].Incoming.noresponse}</td>
                                    <td>{localitySurvey[location].Incoming.durationTotal}</td>                             
                                    <td>{localitySurvey[location].Incoming.averageduration}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <Loader/>
            )}
        </div>
    );
}